/**
 * StatusChip.tsx — 状态徽章（移植自原型 primitives.jsx §2.2 StatusChip）
 *
 * 状态 → 色调 → chip-* class 的映射收口一处，覆盖书籍/App/订单/退款/反馈/推送等常见状态。
 * STATUS_TONE：业务状态字面量 → tone；TONE_CLASS：tone → tokens.css 里的 chip-* 原子类。
 * 调用方可传 tone 显式覆盖；dot 渲染前导圆点；label 覆盖文案；size="sm" 缩字号到 10。
 */

import type { CSSProperties } from 'react';

/** 色调闭集 — 与 TONE_CLASS 的键一一对应 */
export type StatusTone = 'pos' | 'warn' | 'paused-warn' | 'neg' | 'info' | 'accent' | 'default';

/** 业务状态字面量 → 色调（逐字搬蓝本，含原型里 "已退款"=pos / "连载中"=info / "已暂停"=paused-warn 等约定）*/
export const STATUS_TONE: Record<string, StatusTone> = {
  // pos
  上架: 'pos',
  运营中: 'pos',
  active: 'pos',
  成功: 'pos',
  已退款: 'pos',
  已连接: 'pos',
  已发布: 'pos',
  正常: 'pos',
  已完结: 'pos',
  已处理: 'pos',
  连载中: 'info',
  // warn
  下架: 'warn',
  已暂停: 'paused-warn',
  paused: 'warn',
  待上架: 'warn',
  处理中: 'warn',
  待回复: 'warn',
  待审核: 'warn',
  // neg
  已驳回: 'neg',
  已封禁: 'neg',
  失败: 'neg',
  异常: 'neg',
  已拒绝: 'neg',
  // info
  草稿: 'info',
  风控观察: 'info',
  进行中: 'info',
  已回复: 'info',
};

/** 色调 → chip-* 原子类（paused-warn 复用 chip-warn；default 无附加类）*/
export const TONE_CLASS: Record<StatusTone, string> = {
  pos: 'chip-pos',
  warn: 'chip-warn',
  'paused-warn': 'chip-warn',
  neg: 'chip-neg',
  info: 'chip-info',
  accent: 'chip-accent',
  default: '',
};

export interface StatusChipProps {
  /** 业务状态字面量；未命中 STATUS_TONE 时落 default（无色调）*/
  status?: string;
  /** 显式色调，优先级高于 status→tone 推导 */
  tone?: StatusTone;
  /** 渲染前导圆点 ● */
  dot?: boolean;
  /** 覆盖展示文案；缺省回退到 status */
  label?: string;
  style?: CSSProperties;
  /** "sm" 缩字号到 10 */
  size?: 'sm';
}

export function StatusChip({ status, tone, dot, label, style, size }: StatusChipProps) {
  const t = tone || (status !== undefined ? STATUS_TONE[status] : undefined) || 'default';
  const cls = TONE_CLASS[t] || '';
  return (
    <span
      className={`chip ${cls}`}
      style={{ ...(size === 'sm' ? { fontSize: 10 } : null), ...style }}
    >
      {dot && <span style={{ marginRight: 1 }}>●</span>}
      {label ?? status}
    </span>
  );
}
